import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Dimensions,
  ImageBackground,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  useWindowDimensions as useRNWindowDimensions,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Picker } from "@react-native-picker/picker";
import { gql, useMutation, useQuery, useSubscription } from "@apollo/client";
import { MaterialIcons } from "@expo/vector-icons";
import { useLinkTo } from "@react-navigation/native";
import { Text, View } from "../components/Themed";
import Config from "../constants/Config";
import useColorScheme from "../hooks/useColorScheme";
import { getProductImageUrl } from "../utils/urls";
import PublicFooter from "../components/PublicFooter";
import i18n from "../utils/localization";

const ONLINE_PAYMENT_ENABLED =
  process.env.EXPO_PUBLIC_ENABLE_MYPOS === "true" ||
  process.env.EXPO_PUBLIC_ENABLE_STRIPE === "true";

const ONLINE_MENU = gql`
  query OnlineMenu($location: ID, $channel: String) {
    onlineMenu(location: $location, channel: $channel) {
      currency
      locations {
        id
        name
        city
      }
      categories {
        id
        name
        order
      }
      products {
        id
        category
        title
        subtitle
        imageUrls
        prices
        variants
        volumes
        capabilities
        allergens
        options {
          id
          title
          type
          required
          options {
            id
            title
            prices
          }
        }
      }
    }
  }
`;

const ONLINE_ORDER_STATUS = gql`
  query OnlineOrderStatus($orderId: ID!, $publicToken: String!) {
    onlineOrder(id: $orderId, publicToken: $publicToken) {
      id
      publicToken
      orderNumber
      channel
      status
      paymentMethod
      paymentStatus
      createdAt
      updatedAt
      completedAt
      contactConfirmation {
        required
        channel
        destinationMasked
        confirmedAt
        expiresAt
        attempts
        maxAttempts
      }
      customer {
        name
        phone
        email
      }
      fulfillment {
        pickupTime
        city
        street
        comment
        deliveryInstructions
        deliveryFee
      }
      items {
        productId
        title
        variant
        quantity
        unitGrossPrice
        grossTotal
        optionTitles
        comment
      }
      totals {
        itemsGross
        deliveryFee
        orderGross
      }
    }
  }
`;

const ONLINE_ORDER_SUBSCRIPTION = gql`
  subscription OnlineOrderStatus($orderId: ID!, $publicToken: String!) {
    onlineOrder(id: $orderId, publicToken: $publicToken) {
      id
      publicToken
      orderNumber
      channel
      status
      paymentMethod
      paymentStatus
      createdAt
      updatedAt
      completedAt
      contactConfirmation {
        required
        channel
        destinationMasked
        confirmedAt
        expiresAt
        attempts
        maxAttempts
      }
      customer {
        name
        phone
        email
      }
      fulfillment {
        pickupTime
        city
        street
        comment
        deliveryInstructions
        deliveryFee
      }
      items {
        productId
        title
        variant
        quantity
        unitGrossPrice
        grossTotal
        optionTitles
        comment
      }
      totals {
        itemsGross
        deliveryFee
        orderGross
      }
    }
  }
`;

const CREATE_ONLINE_ORDER = gql`
  mutation CreateOnlineOrder($input: OnlineOrderInput!) {
    createOnlineOrder(input: $input) {
      id
      publicToken
      orderNumber
      channel
      status
      paymentMethod
      paymentStatus
      contactConfirmation {
        required
        channel
        destinationMasked
        confirmedAt
        expiresAt
        attempts
        maxAttempts
      }
      totals {
        orderGross
      }
    }
  }
`;

const CONFIRM_ONLINE_ORDER_CONTACT = gql`
  mutation ConfirmOnlineOrderContact($orderId: ID!, $publicToken: String!, $code: String!) {
    confirmOnlineOrderContact(orderId: $orderId, publicToken: $publicToken, code: $code) {
      id
      publicToken
      orderNumber
      status
      paymentMethod
      paymentStatus
      contactConfirmation {
        required
        channel
        destinationMasked
        confirmedAt
        expiresAt
        attempts
        maxAttempts
      }
    }
  }
`;

const RESEND_ONLINE_ORDER_CONTACT_CODE = gql`
  mutation ResendOnlineOrderContactCode($orderId: ID!, $publicToken: String!) {
    resendOnlineOrderContactCode(orderId: $orderId, publicToken: $publicToken) {
      id
      publicToken
      orderNumber
      status
      contactConfirmation {
        required
        channel
        destinationMasked
        expiresAt
        attempts
        maxAttempts
      }
    }
  }
`;

const STATUS_LABELS = {
  awaiting_contact_confirmation: "Confirm contact",
  submitted_unpaid: "Submitted",
  awaiting_payment: "Awaiting payment",
  accepted: "Accepted",
  preparing: "Preparing",
  ready: "Ready",
  out_for_delivery: "Out for delivery",
  completed: "Completed",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

function useResponsiveWindow() {
  return typeof useRNWindowDimensions === "function"
    ? useRNWindowDimensions()
    : Dimensions.get("window");
}

function createMoneyFormatter(currencyCode) {
  const safeCurrency = String(currencyCode || "EUR").toUpperCase();
  try {
    return new Intl.NumberFormat("bg-BG", {
      style: "currency",
      currency: safeCurrency,
      minimumFractionDigits: 2,
    });
  } catch (error) {
    return {
      format(value) {
        return `${safeCurrency} ${Number(value || 0).toFixed(2)}`;
      },
    };
  }
}

function getVariantIndex(product, selection) {
  const variants = product.variants || [""];
  const candidate = Number(selection?.variantIndex || 0);
  if (candidate >= 0 && candidate < variants.length) return candidate;
  return 0;
}

function buildSelectedOptionIds(product, selection, variantIndex, includeRequiredDefaults) {
  const result = [];
  const selections = selection?.options || {};
  for (const option of product.options || []) {
    const raw = selections[option.id];
    if (option.type === "add") {
      const choiceIds = Array.isArray(raw) ? raw : [];
      for (const id of choiceIds) {
        const choice = option.options?.find((c) => c.id === id);
        if (choice) result.push(option.id, choice.id);
      }
      continue;
    }
    let choice = option.options?.find((candidate) => candidate.id === raw);
    if (!choice && includeRequiredDefaults && option.required) {
      choice = option.options?.[0];
    }
    if (!choice) continue;
    result.push(option.id, choice.id);
  }
  return result;
}

function calculateUnitPrice(product, selection, variantIndex) {
  const basePrice = Number(product.prices?.[variantIndex] ?? product.prices?.[0] ?? 0);
  const optionIds = buildSelectedOptionIds(product, selection, variantIndex, true);
  let optionsPrice = 0;
  for (let i = 0; i < optionIds.length; i += 2) {
    const option = product.options?.find((candidate) => candidate.id === optionIds[i]);
    const choice = option?.options?.find((candidate) => candidate.id === optionIds[i + 1]);
    optionsPrice += Number(choice?.prices?.[variantIndex] ?? choice?.prices?.[0] ?? 0);
  }
  return basePrice + optionsPrice;
}

function statusLabel(status) {
  const label = STATUS_LABELS[status] || status || "Submitted";
  return i18n.t(label);
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function OnlineOrderStatusView({ orderId, publicToken, navigation }) {
  const colorScheme = useColorScheme();
  const [confirmationCode, setConfirmationCode] = useState("");
  const { loading, error, data } = useQuery(ONLINE_ORDER_STATUS, {
    variables: { orderId, publicToken },
    skip: !orderId || !publicToken,
  });
  const { data: subscriptionData } = useSubscription(ONLINE_ORDER_SUBSCRIPTION, {
    variables: { orderId, publicToken },
    skip: !orderId || !publicToken,
  });
  const [confirmContact, { loading: confirming }] = useMutation(CONFIRM_ONLINE_ORDER_CONTACT);
  const [resendCode, { loading: resending }] = useMutation(RESEND_ONLINE_ORDER_CONTACT_CODE);

  const order = subscriptionData?.onlineOrder || data?.onlineOrder;
  const formatPrice = createMoneyFormatter("EUR").format;
  const awaitingContactConfirmation = order?.status === "awaiting_contact_confirmation";
  const confirmationChannel = order?.contactConfirmation?.channel === "sms" ? "sms" : "email";
  const confirmationLabel = confirmationChannel === "email" ? i18n.t("email") : i18n.t("SMS");
  const confirmationDestination = confirmationChannel === "email" ? i18n.t("your email") : i18n.t("your phone");

  const confirmCode = async () => {
    const code = confirmationCode.trim();
    if (!code) {
      Alert.alert(i18n.t("Confirmation code"), `${i18n.t("Enter the confirmation code from the")} ${confirmationLabel}.`);
      return;
    }
    try {
      await confirmContact({ variables: { orderId, publicToken, code } });
      setConfirmationCode("");
    } catch (mutationError) {
      Alert.alert(i18n.t("Confirmation failed"), mutationError.message || i18n.t("Please try again."));
    }
  };

  const resendConfirmationCode = async () => {
    try {
      await resendCode({ variables: { orderId, publicToken } });
      Alert.alert(i18n.t("Code sent"), `${i18n.t("We sent a new confirmation code by")} ${confirmationLabel}.`);
    } catch (mutationError) {
      Alert.alert(i18n.t("Send failed"), mutationError.message || i18n.t("Please try again."));
    }
  };

  if (loading && !order) {
    return (
      <SafeAreaView style={styles.centered}>
        <ActivityIndicator size="large" color={Config.companyColor || "#2563eb"} />
      </SafeAreaView>
    );
  }

  if (error || !order) {
    return (
      <SafeAreaView style={styles.centered}>
        <MaterialIcons name="receipt-long" size={40} color="#64748b" />
        <Text style={styles.emptyTitle}>{i18n.t("Order not found")}</Text>
        <TouchableOpacity style={styles.primaryButton} onPress={() => navigation.navigate("Root")}>
          <Text style={styles.primaryButtonText}>{i18n.t("Back to menu")}</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.screen}>
      <ScrollView contentContainerStyle={styles.statusContainer}>
        <View style={styles.statusHeader}>
          <MaterialIcons name="receipt-long" size={40} color={Config.companyColor || "#2563eb"} />
          <Text style={styles.statusNumber}>{order.orderNumber}</Text>
          <View style={styles.statusPill}>
            <Text style={styles.statusPillText}>{statusLabel(order.status)}</Text>
          </View>
        </View>

        <View style={[styles.summaryPanel, colorScheme === "dark" && styles.panelDark]}>
          <Text style={styles.panelTitle}>{i18n.t("Order")}</Text>
          {awaitingContactConfirmation && (
            <View style={styles.confirmationBox}>
              <MaterialIcons name={confirmationChannel === "email" ? "email" : "sms"} size={22} color={Config.companyColor || "#2563eb"} />
              <View style={styles.confirmationContent}>
                <Text style={styles.cartTitle}>
                  {confirmationChannel === "email" ? i18n.t("Confirm your email") : i18n.t("Confirm your phone")}
                </Text>
                <Text style={styles.mutedText}>
                  {`${i18n.t("Enter the")} ${confirmationLabel} ${i18n.t("code sent to")} ${order.contactConfirmation?.destinationMasked || confirmationDestination}.`}
                </Text>
                <TextInput
                  style={styles.input}
                  placeholder={`${confirmationLabel} ${i18n.t("code")}`}
                  keyboardType="number-pad"
                  value={confirmationCode}
                  onChangeText={setConfirmationCode}
                  maxLength={8}
                />
                <View style={styles.confirmationActions}>
                  <TouchableOpacity
                    style={[styles.primaryButton, confirming && styles.disabledButton]}
                    disabled={confirming}
                    onPress={confirmCode}
                  >
                    <MaterialIcons name="check" size={18} color="white" />
                    <Text style={styles.primaryButtonText}>{confirming ? i18n.t("Checking...") : i18n.t("Confirm")}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.secondaryButton, resending && styles.disabledButton]}
                    disabled={resending}
                    onPress={resendConfirmationCode}
                  >
                    <MaterialIcons name="refresh" size={18} color={Config.companyColor || "#2563eb"} />
                    <Text style={styles.secondaryButtonText}>{resending ? i18n.t("Sending...") : i18n.t("Resend")}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          )}
          {(order.items || []).map((item, index) => (
            <View key={`${item.productId}-${index}`} style={styles.lineItem}>
              <View style={styles.lineItemText}>
                <Text style={styles.lineItemTitle}>{item.quantity} x {item.title}</Text>
                {!!item.variant && <Text style={styles.mutedText}>{item.variant}</Text>}
                {!!item.optionTitles?.length && <Text style={styles.mutedText}>{item.optionTitles.join(", ")}</Text>}
              </View>
              <Text style={styles.lineItemPrice}>{formatPrice(item.grossTotal)}</Text>
            </View>
          ))}
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>{i18n.t("Total")}</Text>
            <Text style={styles.totalValue}>{formatPrice(order.totals.orderGross)}</Text>
          </View>
          <Text style={styles.mutedText}>
            {order.paymentStatus === "paid"
              ? i18n.t("Paid")
              : order.channel === "delivery" ? i18n.t("Payment due on delivery") : i18n.t("Payment due at pickup")}
          </Text>
        </View>
        <PublicFooter />
      </ScrollView>
    </SafeAreaView>
  );
}

export default function OnlineOrderScreen({ route, navigation }) {
  const linkTo = useLinkTo();
  const colorScheme = useColorScheme();
  const window = useResponsiveWindow();
  const routeParams = route?.params || {};
  const statusOrderId = routeParams.orderId;
  const statusToken = routeParams.publicToken;
  const focusProductId = routeParams.product || "";
  const focusedProductRef = useRef("");

  const [channel, setChannel] = useState(routeParams.channel === "delivery" ? "delivery" : "pickup");
  const [locationId, setLocationId] = useState(routeParams.location || "");
  const [categoryId, setCategoryId] = useState("");
  const [cart, setCart] = useState([]);
  const [productSelections, setProductSelections] = useState({});
  const [customer, setCustomer] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    street: "",
    pickupTime: "",
    comment: "",
    deliveryInstructions: "",
  });
  const [confirmationChannel, setConfirmationChannel] = useState("email");
  const [paymentMethod, setPaymentMethod] = useState("pay_at_pickup");
  const [submittedOrder, setSubmittedOrder] = useState(null);
  const [cartExpanded, setCartExpanded] = useState(false);

  const { loading, error, data, refetch } = useQuery(ONLINE_MENU, {
    variables: { location: locationId || null, channel },
  });
  const [createOnlineOrder, { loading: submitting }] = useMutation(CREATE_ONLINE_ORDER);

  useEffect(() => {
    setCategoryId("");
  }, [channel, locationId]);

  useEffect(() => {
    if (!focusProductId) return;
    if (focusedProductRef.current !== focusProductId) {
      focusedProductRef.current = "";
    }
    const product = data?.onlineMenu?.products?.find((p) => p.id === focusProductId);
    if (!product) return;
    if (product.category && product.category !== categoryId) {
      setCategoryId(product.category);
    }
    if (typeof document !== "undefined") {
      const node = document.getElementById(`order-product-${focusProductId}`);
      if (node) {
        focusedProductRef.current = focusProductId;
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [focusProductId, data?.onlineMenu?.products]);

  if (statusOrderId && statusToken) {
    return <OnlineOrderStatusView orderId={statusOrderId} publicToken={statusToken} navigation={navigation} />;
  }

  const menu = data?.onlineMenu;
  const formatPrice = useMemo(() => createMoneyFormatter(menu?.currency || "EUR").format, [menu?.currency]);
  const selectedCategoryId = categoryId || menu?.categories?.[0]?.id || "";
  const productColumns = window.width >= 1160 ? 2 : 1;
  const checkoutColumns = window.width >= 980;

  const updateSelection = (productId, patch) => {
    setProductSelections((prev) => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || {}),
        ...patch,
      },
    }));
  };

  const updateSelectedOption = (productId, option, choiceId) => {
    setProductSelections((prev) => {
      const prevOptions = prev[productId]?.options || {};
      let nextValue;
      if (option.type === "add") {
        const current = Array.isArray(prevOptions[option.id]) ? prevOptions[option.id] : [];
        nextValue = current.includes(choiceId)
          ? current.filter((id) => id !== choiceId)
          : [...current, choiceId];
      } else {
        nextValue = choiceId;
      }
      return {
        ...prev,
        [productId]: {
          ...(prev[productId] || {}),
          options: { ...prevOptions, [option.id]: nextValue },
        },
      };
    });
  };

  const addProduct = (product) => {
    const selection = productSelections[product.id] || {};
    const variantIndex = getVariantIndex(product, selection);
    const optionIds = buildSelectedOptionIds(product, selection, variantIndex, true);
    const variant = product.variants?.[variantIndex] || "";
    const unitGrossPrice = calculateUnitPrice(product, selection, variantIndex);
    const key = [
      product.id,
      variant,
      optionIds.join(":"),
      Date.now(),
    ].join("-");

    setCart((prev) => [
      ...prev,
      {
        key,
        productId: product.id,
        title: product.title,
        variant,
        options: optionIds,
        quantity: 1,
        unitGrossPrice,
        comment: "",
      },
    ]);
  };

  const changeQuantity = (key, delta) => {
    setCart((prev) => prev
      .map((item) => item.key === key ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item)
      .filter((item) => item.quantity > 0));
  };

  const cartSubtotal = cart.reduce((sum, item) => sum + item.unitGrossPrice * item.quantity, 0);
  const deliveryFee = 0;
  const cartTotal = cartSubtotal + (channel === "delivery" ? deliveryFee : 0);
  const paymentOptions = [
    { value: "pay_at_pickup", icon: "payments", label: channel === "delivery" ? i18n.t("Pay on delivery") : i18n.t("Pay at pickup") },
    ...(ONLINE_PAYMENT_ENABLED ? [{ value: "pay_online", icon: "credit-card", label: i18n.t("Pay online") }] : []),
  ];

  const submitOrder = async () => {
    const trimmedName = customer.name.trim();
    const trimmedPhone = customer.phone.trim();
    const trimmedEmail = customer.email.trim();
    const trimmedStreet = customer.street.trim();
    const trimmedCity = customer.city.trim();

    if (!cart.length) {
      Alert.alert(i18n.t("Cart is empty"), i18n.t("Add at least one item before sending the order."));
      return;
    }
    if (!trimmedName) {
      Alert.alert(i18n.t("Contact details"), i18n.t("Name is required."));
      return;
    }
    if (paymentMethod !== "pay_online") {
      if (confirmationChannel === "sms" && !trimmedPhone) {
        Alert.alert(i18n.t("Contact details"), i18n.t("Phone is required for SMS confirmation."));
        return;
      }
      if (confirmationChannel === "email" && !isValidEmail(trimmedEmail)) {
        Alert.alert(i18n.t("Contact details"), i18n.t("A valid email is required for email confirmation."));
        return;
      }
    }
    if (channel === "delivery" && (!trimmedStreet || !trimmedCity)) {
      Alert.alert(i18n.t("Delivery details"), i18n.t("City and street are required for delivery."));
      return;
    }

    const input = {
      location: locationId || null,
      channel,
      paymentMethod,
      confirmationChannel,
      customerName: trimmedName,
      customerPhone: trimmedPhone,
      customerEmail: trimmedEmail,
      pickupTime: customer.pickupTime.trim(),
      city: trimmedCity,
      street: trimmedStreet,
      comment: customer.comment.trim(),
      deliveryInstructions: customer.deliveryInstructions.trim(),
      deliveryFee: channel === "delivery" ? deliveryFee : 0,
      items: cart.map((item) => ({
        productId: item.productId,
        variant: item.variant,
        quantity: item.quantity,
        options: item.options,
        capabilities: [],
        comment: item.comment || "",
      })),
    };

    try {
      const result = await createOnlineOrder({ variables: { input } });
      const order = result.data?.createOnlineOrder;
      if (order) {
        setSubmittedOrder(order);
        setCart([]);
        linkTo(`/order/${order.id}/token/${order.publicToken}`);
      }
    } catch (mutationError) {
      Alert.alert(i18n.t("Order failed"), mutationError.message || i18n.t("Please try again."));
    }
  };

  if (loading && !menu) {
    return (
      <SafeAreaView style={styles.centered}>
        <ActivityIndicator size="large" color={Config.companyColor || "#2563eb"} />
      </SafeAreaView>
    );
  }

  if (error) {
    return (
      <SafeAreaView style={styles.centered}>
        <MaterialIcons name="wifi-off" size={40} color="#64748b" />
        <Text style={styles.emptyTitle}>{i18n.t("Menu unavailable")}</Text>
        <TouchableOpacity style={styles.secondaryButton} onPress={() => refetch()}>
          <MaterialIcons name="refresh" size={18} color={Config.companyColor || "#2563eb"} />
          <Text style={styles.secondaryButtonText}>{i18n.t("Retry")}</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  const filteredProducts = (menu?.products || []).filter((product) => !selectedCategoryId || product.category === selectedCategoryId);

  const cartContent = (
    <>
      <View style={styles.channelSwitch}>
        {["pickup", "delivery"].map((value) => (
          <TouchableOpacity
            key={value}
            style={[styles.channelButton, channel === value && styles.segmentButtonActive]}
            onPress={() => setChannel(value)}
          >
            <MaterialIcons
              name={value === "pickup" ? "storefront" : "delivery-dining"}
              size={18}
              color={channel === value ? "white" : "#334155"}
            />
            <Text style={[styles.segmentText, channel === value && styles.segmentTextActive]}>
              {value === "pickup" ? i18n.t("Pickup") : i18n.t("Delivery")}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
      {cart.length === 0 ? (
        <View style={styles.emptyCart}>
          <MaterialIcons name="shopping-cart" size={32} color="#94a3b8" />
          <Text style={styles.mutedText}>{i18n.t("No items yet")}</Text>
        </View>
      ) : (
        cart.map((item) => (
          <View key={item.key} style={styles.cartItem}>
            <View style={styles.cartItemInfo}>
              <Text style={styles.cartTitle}>{item.title}</Text>
              {!!item.variant && <Text style={styles.mutedText}>{item.variant}</Text>}
              <Text style={styles.mutedText}>{formatPrice(item.unitGrossPrice)}</Text>
            </View>
            <View style={styles.quantityControls}>
              <TouchableOpacity style={styles.iconButton} onPress={() => changeQuantity(item.key, -1)}>
                <MaterialIcons name="remove" size={18} color="#334155" />
              </TouchableOpacity>
              <Text style={styles.quantityText}>{item.quantity}</Text>
              <TouchableOpacity style={styles.iconButton} onPress={() => changeQuantity(item.key, 1)}>
                <MaterialIcons name="add" size={18} color="#334155" />
              </TouchableOpacity>
            </View>
          </View>
        ))
      )}

      <View style={styles.totalBox}>
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>{i18n.t("Items")}</Text>
          <Text style={styles.totalValue}>{formatPrice(cartSubtotal)}</Text>
        </View>
        {channel === "delivery" && (
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>{i18n.t("Delivery")}</Text>
            <Text style={styles.totalValue}>{formatPrice(deliveryFee)}</Text>
          </View>
        )}
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>{i18n.t("Total")}</Text>
          <Text style={styles.totalValue}>{formatPrice(cartTotal)}</Text>
        </View>
      </View>

      <Text style={styles.panelTitle}>{i18n.t("Contact")}</Text>
      <TextInput
        style={styles.input}
        placeholder={i18n.t("Name")}
        value={customer.name}
        onChangeText={(value) => setCustomer((prev) => ({ ...prev, name: value }))}
      />
      <TextInput
        style={styles.input}
        placeholder={i18n.t("Phone")}
        keyboardType="phone-pad"
        value={customer.phone}
        onChangeText={(value) => setCustomer((prev) => ({ ...prev, phone: value }))}
      />
      <TextInput
        style={styles.input}
        placeholder={i18n.t("Email")}
        keyboardType="email-address"
        value={customer.email}
        onChangeText={(value) => setCustomer((prev) => ({ ...prev, email: value }))}
      />
      <View style={styles.contactMethodBox}>
          <Text style={styles.cartTitle}>{i18n.t("Payment")}</Text>
          <View style={styles.confirmationMethodRow}>
          {paymentOptions.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={[
                styles.confirmationMethodButton,
                paymentMethod === option.value && styles.segmentButtonActive,
              ]}
              onPress={() => setPaymentMethod(option.value)}
            >
              <MaterialIcons
                name={option.icon}
                size={18}
                color={paymentMethod === option.value ? "white" : "#334155"}
              />
              <Text style={[
                styles.segmentText,
                paymentMethod === option.value && styles.segmentTextActive,
              ]}>
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
      {paymentMethod !== "pay_online" && (
        <View style={styles.contactMethodBox}>
          <Text style={styles.cartTitle}>{i18n.t("Confirm with")}</Text>
          <View style={styles.confirmationMethodRow}>
            {["email", "sms"].map((value) => (
              <TouchableOpacity
                key={value}
                style={[
                  styles.confirmationMethodButton,
                  confirmationChannel === value && styles.segmentButtonActive,
                ]}
                onPress={() => setConfirmationChannel(value)}
              >
                <MaterialIcons
                  name={value === "sms" ? "sms" : "email"}
                  size={18}
                  color={confirmationChannel === value ? "white" : "#334155"}
                />
                <Text style={[
                  styles.segmentText,
                  confirmationChannel === value && styles.segmentTextActive,
                ]}>
                  {value === "sms" ? i18n.t("SMS") : i18n.t("Email")}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}
      {channel === "pickup" ? (
        <TextInput
          style={styles.input}
          placeholder={i18n.t("Pickup time")}
          value={customer.pickupTime}
          onChangeText={(value) => setCustomer((prev) => ({ ...prev, pickupTime: value }))}
        />
      ) : (
        <>
          <TextInput
            style={styles.input}
            placeholder={i18n.t("City")}
            value={customer.city}
            onChangeText={(value) => setCustomer((prev) => ({ ...prev, city: value }))}
          />
          <TextInput
            style={styles.input}
            placeholder={i18n.t("Street and number")}
            value={customer.street}
            onChangeText={(value) => setCustomer((prev) => ({ ...prev, street: value }))}
          />
          <TextInput
            style={styles.input}
            placeholder={i18n.t("Delivery instructions")}
            value={customer.deliveryInstructions}
            onChangeText={(value) => setCustomer((prev) => ({ ...prev, deliveryInstructions: value }))}
          />
        </>
      )}
      <TextInput
        style={[styles.input, styles.commentInput]}
        placeholder={i18n.t("Order notes")}
        value={customer.comment}
        multiline
        onChangeText={(value) => setCustomer((prev) => ({ ...prev, comment: value }))}
      />

      <TouchableOpacity
        style={[styles.primaryButton, submitting && styles.disabledButton]}
        disabled={submitting}
        onPress={submitOrder}
      >
        <MaterialIcons name="send" size={18} color="white" />
        <Text style={styles.primaryButtonText}>
          {submitting ? i18n.t("Sending...") : `${i18n.t("Send order")} ${formatPrice(cartTotal)}`}
        </Text>
      </TouchableOpacity>
    </>
  );

  return (
    <SafeAreaView style={styles.screen}>
      <ScrollView contentContainerStyle={[styles.container, !checkoutColumns && styles.containerMobileCart]}>
        <View style={styles.header}>
          <View>
            <Text style={styles.title}>{i18n.t("Order from")} {Config.orderBrand || Config.menuTitle}</Text>
            <Text style={styles.subtitle}>{i18n.t("Pickup and delivery orders")}</Text>
          </View>
          {submittedOrder && (
            <View style={styles.statusPill}>
              <Text style={styles.statusPillText}>{submittedOrder.orderNumber}</Text>
            </View>
          )}
        </View>

        {!!menu?.categories?.length && (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.categoryTabs}>
            {menu.categories.map((category) => (
              <TouchableOpacity
                key={category.id}
                style={[styles.categoryTab, selectedCategoryId === category.id && styles.categoryTabActive]}
                onPress={() => setCategoryId(category.id)}
              >
                <Text style={[styles.categoryText, selectedCategoryId === category.id && styles.categoryTextActive]}>
                  {category.name}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        )}

        <View style={[styles.main, checkoutColumns && styles.mainWide]}>
          <View style={[styles.menuColumn, checkoutColumns && styles.menuColumnWide]}>
            <View style={[styles.productsGrid, productColumns === 2 && styles.productsGridWide]}>
              {filteredProducts.map((product) => {
                const selection = productSelections[product.id] || {};
                const variantIndex = getVariantIndex(product, selection);
                const unitPrice = calculateUnitPrice(product, selection, variantIndex);
                const variants = product.variants || [""];
                return (
                  <View
                    key={product.id}
                    nativeID={`order-product-${product.id}`}
                    style={[styles.productTile, productColumns === 2 && styles.productTileWide, colorScheme === "dark" && styles.panelDark]}
                    onLayout={() => {
                      if (focusProductId !== product.id || focusedProductRef.current === product.id) return;
                      if (typeof document === "undefined") return;
                      const node = document.getElementById(`order-product-${product.id}`);
                      if (!node) return;
                      focusedProductRef.current = product.id;
                      node.scrollIntoView({ behavior: "smooth", block: "center" });
                    }}
                  >
                    {product.imageUrls?.[0] && (
                      <ImageBackground
                        source={{ uri: getProductImageUrl(product.imageUrls[0], { width: 480, height: 300, fit: "cover" }) }}
                        style={styles.productImage}
                        imageStyle={styles.productImageRadius}
                      />
                    )}
                    <View style={styles.productBody}>
                      <View style={styles.productTitleRow}>
                        <View style={styles.productTitleWrap}>
                          <Text style={styles.productTitle}>{product.title}</Text>
                          {!!product.subtitle && <Text style={styles.productSubtitle}>{product.subtitle}</Text>}
                        </View>
                        <Text style={styles.productPrice}>{formatPrice(unitPrice)}</Text>
                      </View>

                      {variants.length > 1 && (
                        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.variantRow}>
                          {variants.map((variant, index) => (
                            <TouchableOpacity
                              key={`${product.id}-${variant || index}`}
                              style={[styles.choiceButton, variantIndex === index && styles.choiceButtonActive]}
                              onPress={() => updateSelection(product.id, { variantIndex: index })}
                            >
                              <Text style={[styles.choiceText, variantIndex === index && styles.choiceTextActive]}>
                                {variant || i18n.t("Default")}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </ScrollView>
                      )}

                      {(product.options || []).map((option) => (
                        <View key={option.id} style={styles.optionGroup}>
                          <Text style={styles.optionTitle}>{option.title}</Text>
                          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.variantRow}>
                            {(option.options || []).map((choice) => {
                              const raw = selection.options?.[option.id];
                              const active = option.type === "add"
                                ? Array.isArray(raw) && raw.includes(choice.id)
                                : (raw || (option.required ? option.options?.[0]?.id : null)) === choice.id;
                              return (
                                <TouchableOpacity
                                  key={choice.id}
                                  style={[styles.choiceButton, active && styles.choiceButtonActive]}
                                  onPress={() => updateSelectedOption(product.id, option, choice.id)}
                                >
                                  <Text style={[styles.choiceText, active && styles.choiceTextActive]}>{choice.title}</Text>
                                </TouchableOpacity>
                              );
                            })}
                          </ScrollView>
                        </View>
                      ))}

                      <TouchableOpacity style={styles.addButton} onPress={() => addProduct(product)}>
                        <MaterialIcons name="add-shopping-cart" size={18} color="white" />
                        <Text style={styles.addButtonText}>{i18n.t("Add")}</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>

          {checkoutColumns && (
            <View style={[styles.checkoutColumn, styles.checkoutColumnWide, colorScheme === "dark" && styles.panelDark]}>
              <Text style={styles.panelTitle}>{i18n.t("Cart")}</Text>
              {cartContent}
            </View>
          )}
        </View>
        <PublicFooter />
      </ScrollView>

      {!checkoutColumns && (
        cartExpanded ? (
          <View style={styles.mobileCartSheet}>
            <View style={styles.mobileCartHandle}>
              <Text style={styles.panelTitle}>{i18n.t("Cart")}</Text>
              <TouchableOpacity style={styles.mobileCartClose} onPress={() => setCartExpanded(false)}>
                <MaterialIcons name="expand-more" size={24} color="#334155" />
              </TouchableOpacity>
            </View>
            <ScrollView contentContainerStyle={styles.mobileCartContent} keyboardShouldPersistTaps="handled">
              {cartContent}
            </ScrollView>
          </View>
        ) : (
          <TouchableOpacity style={styles.mobileCartBar} onPress={() => setCartExpanded(true)}>
            <MaterialIcons name="shopping-cart" size={22} color="white" />
            <View style={styles.mobileCartBarInfo}>
              <Text style={styles.mobileCartBarText}>
                {cart.length === 0
                  ? i18n.t("Cart")
                  : `${cart.length} ${cart.length === 1 ? i18n.t("item") : i18n.t("items")}`}
              </Text>
              {cart.length > 0 && (
                <Text style={styles.mobileCartBarTotal}>{formatPrice(cartTotal)}</Text>
              )}
            </View>
            <MaterialIcons name="expand-less" size={24} color="white" />
          </TouchableOpacity>
        )
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: "#f1f5f9",
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    padding: 24,
  },
  container: {
    padding: 16,
    gap: 14,
  },
  containerMobileCart: {
    paddingBottom: 96,
  },
  mobileCartBar: {
    position: "absolute",
    left: 12,
    right: 12,
    bottom: 16,
    height: 58,
    borderRadius: 12,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    gap: 12,
    backgroundColor: Config.companyColor || "#2563eb",
    shadowColor: "#000",
    shadowOpacity: 0.18,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 6,
  },
  mobileCartBarInfo: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    backgroundColor: "transparent",
  },
  mobileCartBarText: {
    color: "white",
    fontWeight: "800",
    fontSize: 15,
  },
  mobileCartBarTotal: {
    color: "white",
    fontWeight: "900",
    fontSize: 16,
  },
  mobileCartSheet: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: "white",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    shadowColor: "#000",
    shadowOpacity: 0.18,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: -2 },
    elevation: 12,
  },
  mobileCartHandle: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
  },
  mobileCartClose: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f1f5f9",
  },
  mobileCartContent: {
    padding: 16,
    gap: 10,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  title: {
    fontSize: 28,
    fontWeight: "700",
  },
  subtitle: {
    color: "#64748b",
    marginTop: 2,
  },
  channelSwitch: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderRadius: 8,
    overflow: "hidden",
  },
  channelButton: {
    flex: 1,
    minHeight: 42,
    paddingHorizontal: 14,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 6,
    backgroundColor: "#f8fafc",
  },
  segmentButtonActive: {
    backgroundColor: Config.companyColor || "#2563eb",
  },
  segmentText: {
    color: "#334155",
    fontWeight: "700",
  },
  segmentTextActive: {
    color: "white",
  },
  locationPicker: {
    minWidth: 240,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#f8fafc",
  },
  picker: {
    height: 42,
  },
  categoryTabs: {
    gap: 8,
    paddingVertical: 2,
  },
  categoryTab: {
    paddingHorizontal: 14,
    height: 38,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#cbd5e1",
    backgroundColor: "#f8fafc",
  },
  categoryTabActive: {
    borderColor: Config.companyColor || "#2563eb",
    backgroundColor: "#eff6ff",
  },
  categoryText: {
    fontWeight: "700",
    color: "#334155",
  },
  categoryTextActive: {
    color: Config.companyColor || "#2563eb",
  },
  main: {
    gap: 14,
  },
  mainWide: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  menuColumn: {
    flex: 1,
  },
  menuColumnWide: {
    flex: 1,
    minWidth: 0,
  },
  productsGrid: {
    gap: 12,
  },
  productsGridWide: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  productTile: {
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "white",
    width: "100%",
  },
  productTileWide: {
    flexBasis: "48%",
    flexGrow: 1,
    minWidth: 300,
    width: undefined,
  },
  panelDark: {
    borderColor: "#334155",
  },
  productImage: {
    height: 132,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f1f5f9",
  },
  productImageRadius: {
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  productBody: {
    padding: 12,
    gap: 10,
  },
  productTitleRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
  },
  productTitleWrap: {
    flex: 1,
  },
  productTitle: {
    fontSize: 16,
    fontWeight: "700",
  },
  productSubtitle: {
    marginTop: 2,
    color: "#64748b",
  },
  productPrice: {
    fontSize: 16,
    fontWeight: "800",
  },
  variantRow: {
    flexDirection: "row",
    gap: 8,
    paddingVertical: 2,
  },
  choiceButton: {
    minHeight: 34,
    borderRadius: 8,
    paddingHorizontal: 10,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#cbd5e1",
    backgroundColor: "#f8fafc",
  },
  choiceButtonActive: {
    borderColor: Config.companyColor || "#2563eb",
    backgroundColor: Config.companyColor || "#2563eb",
  },
  choiceText: {
    color: "#334155",
    fontWeight: "700",
  },
  choiceTextActive: {
    color: "white",
  },
  optionGroup: {
    gap: 4,
  },
  optionTitle: {
    fontSize: 12,
    color: "#64748b",
    fontWeight: "700",
    textTransform: "uppercase",
  },
  addButton: {
    height: 42,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 8,
    backgroundColor: Config.companyColor || "#2563eb",
  },
  addButtonText: {
    color: "white",
    fontWeight: "800",
  },
  checkoutColumn: {
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 8,
    padding: 14,
    gap: 10,
    backgroundColor: "white",
    width: "100%",
  },
  checkoutColumnWide: {
    maxWidth: 380,
  },
  panelTitle: {
    fontSize: 18,
    fontWeight: "800",
  },
  emptyCart: {
    minHeight: 90,
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 8,
  },
  mutedText: {
    color: "#64748b",
    fontSize: 13,
  },
  cartItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
  },
  cartItemInfo: {
    flex: 1,
    minWidth: 0,
  },
  cartTitle: {
    fontWeight: "700",
  },
  quantityControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  iconButton: {
    width: 34,
    height: 34,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f1f5f9",
    borderWidth: 1,
    borderColor: "#cbd5e1",
  },
  quantityText: {
    minWidth: 18,
    textAlign: "center",
    fontWeight: "800",
  },
  totalBox: {
    gap: 6,
    paddingVertical: 10,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
  },
  totalLabel: {
    fontWeight: "700",
  },
  totalValue: {
    fontSize: 18,
    fontWeight: "900",
  },
  input: {
    minHeight: 42,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderRadius: 8,
    paddingHorizontal: 12,
    backgroundColor: "#f8fafc",
    color: "#0f172a",
  },
  contactMethodBox: {
    gap: 8,
  },
  confirmationMethodRow: {
    flexDirection: "row",
    gap: 8,
  },
  confirmationMethodButton: {
    minHeight: 40,
    flex: 1,
    paddingHorizontal: 10,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    backgroundColor: "#f8fafc",
  },
  commentInput: {
    minHeight: 72,
    paddingTop: 10,
    textAlignVertical: "top",
  },
  paymentBox: {
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderRadius: 8,
    padding: 10,
    flexDirection: "row",
    gap: 10,
    alignItems: "center",
    backgroundColor: "#f8fafc",
  },
  paymentText: {
    flex: 1,
    minWidth: 0,
  },
  confirmationBox: {
    borderWidth: 1,
    borderColor: "#bfdbfe",
    borderRadius: 8,
    padding: 12,
    flexDirection: "row",
    gap: 10,
    backgroundColor: "#eff6ff",
  },
  confirmationContent: {
    flex: 1,
    minWidth: 0,
    gap: 10,
    backgroundColor: "transparent",
  },
  confirmationActions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    backgroundColor: "transparent",
  },
  primaryButton: {
    minHeight: 46,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 8,
    backgroundColor: Config.companyColor || "#2563eb",
    paddingHorizontal: 14,
  },
  disabledButton: {
    opacity: 0.6,
  },
  primaryButtonText: {
    color: "white",
    fontWeight: "900",
  },
  secondaryButton: {
    minHeight: 42,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 8,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: "#cbd5e1",
  },
  secondaryButtonText: {
    color: Config.companyColor || "#2563eb",
    fontWeight: "800",
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: "800",
  },
  statusContainer: {
    padding: 16,
    gap: 14,
    maxWidth: 720,
    alignSelf: "center",
    width: "100%",
  },
  statusHeader: {
    alignItems: "center",
    gap: 10,
    paddingVertical: 20,
  },
  statusNumber: {
    fontSize: 28,
    fontWeight: "900",
  },
  statusPill: {
    paddingHorizontal: 12,
    height: 34,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#e0f2fe",
  },
  statusPillText: {
    color: "#0369a1",
    fontWeight: "800",
  },
  summaryPanel: {
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 8,
    padding: 14,
    gap: 10,
    backgroundColor: "white",
  },
  lineItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 10,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
  },
  lineItemText: {
    flex: 1,
    minWidth: 0,
  },
  lineItemTitle: {
    fontWeight: "700",
  },
  lineItemPrice: {
    fontWeight: "800",
  },
});
